// Filter and sort flyout -- one dialog for every viewport -- SG 20261019
// Opens from the bar trigger, traps focus, closes on X, overlay, Escape or Show results.
// Filter groups start collapsed; the groups a shopper opens are remembered for the visit.
import { GeneralOverlay } from "../general/general-overlay";
import { bindFacetPopstate, buildFilterUrl, FACETS_RENDERED_EVENT, navigateFacets, setGroupOpen } from "./facet-action";
import { FacetBarScroll } from "./facet-bar";

const OPEN_GROUPS_KEY = `ltd-facets-open:${window.location.pathname}`;

class CollectionFacets extends HTMLElement {
  private abortController = new AbortController();
  private barScroll: FacetBarScroll | null = null;
  private isOpen = false;

  connectedCallback() {
    this.abortController = new AbortController();
    const opts = { signal: this.abortController.signal };
    this.addEventListener("click", this.onClick.bind(this), opts);
    this.addEventListener("change", this.onChange.bind(this), opts);
    // On the document, so Escape still closes the flyout when focus has left it -- SG 20261020
    document.addEventListener("keydown", this.onKeydown.bind(this), opts);
    this.addEventListener(FACETS_RENDERED_EVENT, () => this.restoreOpenGroups(), opts);
    document.addEventListener("facets:close", () => this.close(false), opts);

    this.restoreOpenGroups();
    bindFacetPopstate();

    const bar = this.querySelector<HTMLElement>(".collection__facets-bar");
    const sentinel = this.querySelector<HTMLElement>(".collection__facets-sentinel");
    if (bar && sentinel) {
      this.barScroll = new FacetBarScroll(bar, sentinel);
      this.barScroll.start();
    }
  }

  disconnectedCallback() {
    this.abortController.abort();
    this.barScroll?.stop();
    if (this.isOpen) this.close(false);
  }

  get drawer() {
    return this.querySelector<HTMLElement>(".collection__facets-drawer");
  }

  get trigger() {
    return this.querySelector<HTMLElement>(".collection__facets-trigger");
  }

  onClick(event: Event) {
    const target = (event.target as HTMLElement).closest<HTMLElement>("[data-facet-action], [data-facet-toggle]");
    if (!target || !this.contains(target)) return;

    if (target.hasAttribute("data-facet-toggle")) {
      this.toggleGroup(target);
      return;
    }

    switch (target.getAttribute("data-facet-action")) {
      case "open":
        this.open();
        break;
      case "close":
        this.close(true);
        break;
      case "show":
        this.close(true);
        this.barScroll?.scrollToResults();
        break;
      case "remove":
        this.removeChip(target);
        break;
      case "clear":
        navigateFacets(target.getAttribute("data-remove-url"));
        break;
    }
  }

  onChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const action = input.getAttribute("data-facet-action");
    // Safari focuses nothing on a label click, so focus would sit outside the open flyout -- SG 20261020
    if (this.isOpen && document.activeElement !== input) input.focus({ preventScroll: true });

    if (action === "select") {
      navigateFacets(buildFilterUrl(this));
    } else if (action === "sort" && input.checked) {
      const url = new URL(window.location.href);
      url.searchParams.set("sort_by", input.value);
      url.searchParams.delete("page");
      url.hash = "";
      navigateFacets(url);
    }
  }

  onKeydown(event: KeyboardEvent) {
    if (event.key === "Escape" && this.isOpen) {
      event.stopPropagation();
      this.close(true);
    }
  }

  // Uncheck the matching option and rebuild from live state, so rapid removals compose.
  removeChip(chip: HTMLElement) {
    const name = chip.getAttribute("data-param");
    const value = chip.getAttribute("data-value");
    const input = name
      ? Array.from(this.querySelectorAll<HTMLInputElement>('[data-facets-groups] input[type="checkbox"]')).find(
          (candidate) => candidate.name === name && candidate.value === value
        )
      : undefined;

    if (input) {
      input.checked = false;
      navigateFacets(buildFilterUrl(this));
    } else {
      navigateFacets(chip.getAttribute("data-remove-url"));
    }
  }

  open() {
    const drawer = this.drawer;
    if (!drawer || this.isOpen) return;
    this.isOpen = true;

    drawer.classList.add("open");
    this.trigger?.setAttribute("aria-expanded", "true");
    (document.querySelector("general-overlay") as GeneralOverlay | null)?.applyOverlay(drawer);

    const closeButton = drawer.querySelector<HTMLElement>(".close");
    if (typeof window.trapFocus === "function" && closeButton) {
      window.trapFocus(drawer, closeButton);
    } else {
      closeButton?.focus();
    }
  }

  close(returnFocus: boolean) {
    const drawer = this.drawer;
    if (!drawer || !this.isOpen) return;
    this.isOpen = false;

    drawer.classList.remove("open");
    this.trigger?.setAttribute("aria-expanded", "false");
    (document.querySelector("general-overlay") as GeneralOverlay | null)?.removeOverlay(drawer);

    if (typeof window.removeTrapFocus === "function") window.removeTrapFocus(null);
    // The trigger sits in the pinned bar, but Safari still scrolls to it on focus -- SG 20261026
    if (returnFocus) this.trigger?.focus({ preventScroll: true });
  }

  toggleGroup(toggle: HTMLElement) {
    const group = toggle.closest(".collection__facet");
    if (!group) return;
    setGroupOpen(group, !group.classList.contains("is-open"));
    this.saveOpenGroups();
  }

  saveOpenGroups() {
    const params = Array.from(this.querySelectorAll<HTMLElement>(".collection__facet.is-open")).map(
      (group) => group.dataset.param
    );
    try {
      sessionStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(params));
    } catch {
      // Storage blocked (private mode): groups simply start collapsed next time.
    }
  }

  restoreOpenGroups() {
    let params: string[] = [];
    try {
      const stored = JSON.parse(sessionStorage.getItem(OPEN_GROUPS_KEY) || "[]");
      if (Array.isArray(stored)) params = stored.filter((param) => typeof param === "string");
    } catch {
      params = [];
    }
    this.querySelectorAll<HTMLElement>(".collection__facet").forEach((group) => {
      if (params.indexOf(group.dataset.param || "") !== -1) setGroupOpen(group, true);
    });
  }
}

customElements.define("collection-facets", CollectionFacets);
